import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../auth'
import { Icon } from './ui'
import { CommandPalette } from './CommandPalette'
import { applyTheme, resolveInitialTheme, type Theme } from '../lib/theme'

type NavItem = { to: string; label: string; icon: string; end?: boolean }

// The sidebar is grouped because 30+ flat links are unreadable. Groups collapse and remember
// their state per browser; the first paint opens the group that contains the current route.
const NAV_GROUPS: { key: string; label: string; items: NavItem[] }[] = [
  {
    key: 'audit', label: 'การตรวจสอบ',
    items: [
      { to: '/', label: 'ภาพรวม', icon: 'dashboard', end: true },
      { to: '/board', label: 'คณะกรรมการ', icon: 'board' },
      { to: '/findings', label: 'ข้อสังเกต', icon: 'findings' },
      { to: '/cases', label: 'เคสตรวจสอบ', icon: 'cases' },
      { to: '/engagements', label: 'โครงการตรวจสอบ', icon: 'engagements' },
      { to: '/clients', label: 'นิติบุคคล', icon: 'cases' },
      { to: '/whistle/triage', label: 'แจ้งเบาะแส', icon: 'shield' },
    ],
  },
  {
    key: 'risk', label: 'ความเสี่ยง',
    items: [
      { to: '/vendors', label: 'ผู้ขาย', icon: 'cases' },
      { to: '/contracts', label: 'ทะเบียนสัญญา', icon: 'documents' },
      { to: '/ownership', label: 'โครงสร้างการถือหุ้น', icon: 'cases' },
      { to: '/aml', label: 'AML / STR', icon: 'shield' },
      { to: '/guardrails', label: 'การ์ดเรลเอกสาร', icon: 'shield' },
      { to: '/network', label: 'เครือข่ายคู่ค้า', icon: 'cases' },
      { to: '/inventory', label: 'สินค้าคงคลัง', icon: 'documents' },
      { to: '/payroll', label: 'เงินเดือน', icon: 'billing' },
      { to: '/expenses', label: 'เบิกค่าใช้จ่าย', icon: 'billing' },
      { to: '/cards', label: 'บัตรองค์กร', icon: 'billing' },
    ],
  },
  {
    key: 'docs', label: 'เอกสารและข้อมูล',
    items: [
      { to: '/documents', label: 'เอกสาร', icon: 'documents' },
      { to: '/archives', label: 'คลังเก็บถาวร', icon: 'trail' },
      { to: '/erp', label: 'เชื่อมต่อ ERP', icon: 'documents' },
    ],
  },
  {
    key: 'money', label: 'การเงิน',
    items: [
      { to: '/billing', label: 'การเรียกเก็บเงิน', icon: 'billing' },
      { to: '/payments', label: 'การชำระเงิน', icon: 'billing' },
      { to: '/renewals', label: 'การต่ออายุ', icon: 'billing' },
      { to: '/usage', label: 'การใช้บริการ', icon: 'billing' },
      { to: '/fx', label: 'อัตราแลกเปลี่ยน', icon: 'billing' },
      { to: '/support', label: 'ดูแลลูกค้า', icon: 'bell' },
      { to: '/billing/plan-changes', label: 'เปลี่ยนแพ็กเกจ', icon: 'billing' },
      { to: '/partners', label: 'พาร์ทเนอร์', icon: 'billing' },
      { to: '/pricing', label: 'ตารางราคา', icon: 'billing' },
      { to: '/royalties', label: 'ค่าลิขสิทธิ์', icon: 'billing' },
    ],
  },
  {
    key: 'ops', label: 'ปฏิบัติการและตรวจวัด',
    items: [
      { to: '/live', label: 'เหตุการณ์สด', icon: 'bell' },
      { to: '/traces', label: 'ร่องรอยคำขอ', icon: 'trail' },
      { to: '/analytics', label: 'วิเคราะห์', icon: 'board' },
      { to: '/projections', label: 'โปรเจกชัน', icon: 'board' },
      { to: '/locks', label: 'ล็อก', icon: 'lock' },
      { to: '/shared-limits', label: 'โควต้าร่วม', icon: 'shield' },
      { to: '/cache', label: 'แคช', icon: 'settings' },
      { to: '/siem', label: 'SIEM', icon: 'shield' },
      { to: '/provisioning', label: 'สร้างองค์กรใหม่', icon: 'cases' },

    ],
  },
  {
    key: 'system', label: 'ระบบและกำกับ',
    items: [
      { to: '/audit-trail', label: 'ร่องรอยการตรวจสอบ', icon: 'trail' },
      { to: '/rules', label: 'ทะเบียนกฎ', icon: 'shield' },
      { to: '/health', label: 'สุขภาพระบบ', icon: 'shield' },
      { to: '/jobs', label: 'คิวงาน', icon: 'settings' },
      { to: '/roles', label: 'บทบาทและสิทธิ์', icon: 'shield' },
      { to: '/api-keys', label: 'คีย์ API', icon: 'lock' },
      { to: '/notifications', label: 'การแจ้งเตือน', icon: 'bell' },
      { to: '/feature-flags', label: 'ฟีเจอร์แฟล็ก', icon: 'settings' },
      { to: '/dsar', label: 'คำขอ PDPA', icon: 'shield' },
      { to: '/coverage', label: 'ความครอบคลุม', icon: 'board' },
      { to: '/masking', label: 'นโยบายปิดข้อมูล', icon: 'lock' },
      { to: '/schema', label: 'ทะเบียนไมเกรชัน', icon: 'settings' },
      { to: '/incidents', label: 'เหตุการณ์', icon: 'bell' },
      { to: '/residency', label: 'ถิ่นข้อมูล', icon: 'shield' },
      { to: '/field-crypto', label: 'เข้ารหัสฟิลด์', icon: 'lock' },
      { to: '/vault', label: 'คลังกุญแจ', icon: 'lock' },
      { to: '/licenses', label: 'ไลเซนส์', icon: 'lock' },
      { to: '/access-fences', label: 'รั้วการเข้าถึง', icon: 'shield' },
    ],
  },
]


const titles: Record<string, string> = {
  '/': 'ภาพรวม',
  '/board': 'คณะกรรมการ',
  '/search': 'ค้นหา',
  '/whistle/triage': 'แจ้งเบาะแส',
  '/clients': 'นิติบุคคล',
  '/coverage': 'ความครอบคลุม',
  '/api-keys': 'คีย์ API',
  '/roles': 'บทบาทและสิทธิ์',
  '/support': 'ดูแลลูกค้า',
  '/notifications': 'การแจ้งเตือน',
  '/feature-flags': 'ฟีเจอร์แฟล็ก',
  '/trust': 'ศูนย์ความโปร่งใส',
  '/findings': 'ข้อสังเกต',
  '/engagements': 'โครงการตรวจสอบ',
  '/documents': 'เอกสาร',
  '/cases': 'เคสตรวจสอบ',
  '/billing': 'การเรียกเก็บเงิน',
  '/payments': 'การชำระเงิน',
  '/dsar': 'คำขอใช้สิทธิ PDPA',
  '/vendors': 'ผู้ขาย',
  '/inventory': 'สินค้าคงคลัง',
  '/contracts': 'ทะเบียนสัญญา',
  '/ownership': 'โครงสร้างการถือหุ้น',
  '/aml': 'AML และ STR',
  '/erp': 'เชื่อมต่อ ERP',
  '/guardrails': 'การ์ดเรลเอกสาร',
  '/archives': 'คลังเก็บถาวร',
  '/renewals': 'การต่ออายุบริการ',
  '/usage': 'การใช้บริการ',
  '/fx': 'สกุลเงินและอัตราแลกเปลี่ยน',
  '/jobs': 'คิวงานเบื้องหลัง',
  '/rules': 'ทะเบียนกฎ',
  '/health': 'สุขภาพระบบ',
  '/payroll': 'ทะเบียนเงินเดือน',
  '/expenses': 'เบิกค่าใช้จ่าย',
  '/cards': 'บัตรองค์กร',
  '/network': 'เครือข่ายคู่ค้า',
  '/masking': 'นโยบายปิดข้อมูลส่วนบุคคล',
  '/schema': 'ทะเบียนไมเกรชัน',
  '/billing/plan-changes': 'เปลี่ยนแพ็กเกจ',
  '/partners': 'พาร์ทเนอร์',
  '/pricing': 'ตารางราคาตามประเทศ',
  '/royalties': 'ค่าลิขสิทธิ์',
  '/field-crypto': 'การเข้ารหัสระดับฟิลด์',
  '/incidents': 'เหตุการณ์และ playbook',
  '/residency': 'ถิ่นที่อยู่ของข้อมูล',
  '/licenses': 'ไลเซนส์สำหรับติดตั้งเอง',
  '/access-fences': 'รั้วการเข้าถึง',
  '/vault': 'คลังกุญแจและ rotate',
  '/locks': 'ล็อกแบบกระจาย',
  '/shared-limits': 'โควต้าใช้ร่วมกัน',
  '/siem': 'ส่งออก SIEM',
  '/traces': 'ร่องรอยคำขอ',
  '/live': 'เหตุการณ์สด',
  '/projections': 'โปรเจกชันและ read model',
  '/provisioning': 'สร้างองค์กรใหม่',
  '/analytics': 'วิเคราะห์องค์กร',
  '/cache': 'แคชคำตอบ',
  '/ai-drafts': 'ฉบับร่างที่ AI ช่วยเขียน',
  '/sla': 'เครดิตชดเชย SLA',
  '/forecast': 'พยากรณ์รายได้',
  '/breakers': 'Circuit breaker',
  '/vulnerabilities': 'ช่องโหว่และ dependency',
  '/patches': 'แพตช์และ runbook',
  '/threat-hunting': 'ล่าภัยคุกคาม',
  '/vault-access': 'สิทธิ์เข้าถึงคลังกุญแจ',
  '/compliance': 'ภาระกำกับดูแล',
  '/narratives': 'คำบรรยายรายงานที่ AI ช่วย',
  '/ueba': 'พฤติกรรมผู้ใช้ (UEBA)',
  '/anomalies': 'แจ้งเตือนความผิดปกติ',
  '/llm-cache': 'แคช LLM และค่าใช้จ่าย',
  '/consensus': 'ฉันทามติหลายโมเดล',
  '/streams': 'สตรีมเหตุการณ์',
  '/reconciliation': 'กระทบยอดชำระเงิน',
  '/compliance-ai': 'พยากรณ์การกำกับดูแล',
  '/collusion': 'วงสัมพันธ์ทุจริต',
  '/adaptive-risk': 'ความเสี่ยงที่เรียนรู้',
  '/notify-engine': 'engine แจ้งเตือน',
  '/remediation': 'แผนแก้ไข',
  '/flags-engine': 'แฟล็กและ experiment',
  '/inference': 'โมเดลอนุมาน',
  '/threat-intel': 'ข่าวกรองภัยคุกคาม',
  '/model-registry': 'ทะเบียนโมเดล',
  '/sagas': 'Saga orchestration',
  '/graph': 'กราฟความสัมพันธ์',
  '/load-test': 'ทดสอบโหลด',
  '/simulation': 'จำลองธุรกรรม',
  '/perf': 'สุขภาพฐานข้อมูลและดัชนี',
  '/zero-trust': 'Zero-trust และ microsegmentation',
  '/ip-lists': 'IP allow/block',
  '/thresholds': 'เกณฑ์แจ้งเตือน',
  '/retraining': 'ฝึกโมเดลใหม่',
  '/clustering': 'จัดกลุ่มวงทุจริต',
  '/csr': 'ทุนการศึกษาและ CSR',
  '/franchise': 'แฟรนไชส์และลิขสิทธิ์',
  '/agents': 'เอเจนต์อัตโนมัติ',
  '/query-cache': 'แคชคิวรี',
  '/discovery': 'ค้นหาบริการและ health',
  '/biometric': 'ไบโอเมตริก (passkey)',
  '/partitioning': 'พาร์ทิชันตาราง',
  '/websockets': 'WebSocket',
  '/payment-gateway': 'payment gateway',
  '/event-store': 'event store และ replay',
  '/resources': 'ทรัพยากรเครื่องและ telemetry',
  '/edge-audit': 'ตรวจความปลอดภัย edge/TLS',
  '/ai-master': 'AI master และ self-healing',
  '/replication': 'replication และ DR',
  '/failover': 'failover drill',
  '/orchestration': 'orchestration การ deploy',
  '/cluster': 'K8s manifests',
  '/scenarios': 'scenario การเงิน',
  '/readiness': 'ความพร้อม production',
  '/autoscale': 'autoscaling',
  '/cdn': 'CDN และแคช',
  '/enforcement': 'บังคับนโยบายที่ edge',
  '/geo': 'geo routing',
  '/multicloud': 'ตรวจ multi-cloud',
  '/rollout': 'rollout waves',
  '/logical-replication': 'logical replication',
  '/dr': 'DR controller',
  '/voting': 'voting หลายโมเดล',
  '/release': 'release dossier',
  '/purge': 'ลบข้อมูลตามนโยบายเก็บรักษา',
  '/webhooks': 'webhook และ DLQ',
  '/soak': 'soak test',
  '/security': 'ความปลอดภัย',
  '/audit-trail': 'ร่องรอยการตรวจสอบ',
}

export function AppShell({ children }: { children: ReactNode }) {
  const { me, signOut } = useAuth()
  const location = useLocation()
  const [palette, setPalette] = useState(false)
  const [navQuery, setNavQuery] = useState('')
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('nav.closed') ?? '{}') as Record<string, boolean>
    } catch {
      return {}
    }
  })
  const toggleGroup = (key: string) => {
    setClosedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      localStorage.setItem('nav.closed', JSON.stringify(next))
      return next
    })
  }
  const [theme, setTheme] = useState<Theme>(() => resolveInitialTheme())

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPalette((v) => !v)
      }
      if (e.key === 'Escape') setPalette(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const crumb = (() => {
    if (location.pathname.startsWith('/findings/')) return 'ข้อสังเกต · รายละเอียด'
    if (location.pathname.startsWith('/cases/')) return 'เคสตรวจสอบ · รายละเอียด'
    if (location.pathname.startsWith('/engagements/')) return 'โครงการตรวจสอบ · รายละเอียด'
    return titles[location.pathname] ?? 'ภาพรวม'
  })()

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark" />
          <span className="brand-word">AUDIT<span>AI</span></span>
        </div>
        <nav className="nav nav-scroll">
          <input className="nav-filter" value={navQuery} onChange={(e) => setNavQuery(e.target.value)}
            placeholder="กรองเมนู…" aria-label="กรองเมนู" />
          {navQuery.trim() !== '' ? (
            <div className="nav-group">
              {NAV_GROUPS.flatMap((g) => g.items)
                .filter((i) => i.label.toLowerCase().includes(navQuery.trim().toLowerCase()))
                .map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                    <Icon name={item.icon} />
                    {item.label}
                  </NavLink>
                ))}
            </div>
          ) : NAV_GROUPS.map((g) => {
            const closed = closedGroups[g.key]
            return (
              <div key={g.key} className="nav-group">
                <button type="button" className="nav-group-head" aria-expanded={!closed}
                  onClick={() => toggleGroup(g.key)}>
                  <span className="nav-label">{g.label}</span>
                  <span className={`nav-chevron${closed ? ' closed' : ''}`} aria-hidden="true">⌄</span>
                </button>
                {!closed && g.items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                    <Icon name={item.icon} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>
        <div className="nav-spacer" />
        <div className="tenant-card">
          <div className="tenant-name">{me?.tenant.display_name}</div>
          <div className="tenant-meta">
            {me?.role === 'owner' ? 'เจ้าขององค์กร' : me?.role} · {me?.tenant.base_currency}
          </div>
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, paddingLeft: 0 }} onClick={() => void signOut()}>
            ออกจากระบบ
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="crumbs">
            <span>Audit AI</span>
            <span style={{ opacity: 0.4 }}>/</span>
            <b>{crumb}</b>
          </div>
          <button className="topbar-search" onClick={() => setPalette(true)}>
            <Icon name="search" size={15} />
            ค้นหาเอกสาร ข้อสังเกต หรือคำสั่ง
            <span className="kbd">⌘K</span>
          </button>
          <button
            className="btn btn-ghost btn-sm"
            aria-label={theme === 'dark' ? 'สลับเป็นโหมดสว่าง' : 'สลับเป็นโหมดมืด'}
            title={theme === 'dark' ? 'โหมดสว่าง' : 'โหมดมืด'}
            onClick={() => {
              const next: Theme = theme === 'dark' ? 'light' : 'dark'
              setTheme(next)
              applyTheme(next)
            }}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
          </button>
          <button className="btn btn-ghost btn-sm" aria-label="การแจ้งเตือน"><Icon name="bell" size={17} /></button>
          <div className="avatar" title={me?.user.email}>
            {me?.user.display_name?.slice(0, 1) ?? 'A'}
          </div>
        </header>
        <main className="page">{children}</main>
      </div>

      <CommandPalette
        open={palette}
        onClose={() => setPalette(false)}
        onSignOut={() => { setPalette(false); void signOut() }}
      />
    </div>
  )
}
